/**
 * Class sets every interactive component shares, so focus, press and popups look and behave
 * alike everywhere. Complete class literals, so Tailwind sees them.
 */

/** Keyboard focus: the theme's ring, offset so it never shifts layout. Mouse focus shows none. */
export const FOCUS_RING = "outline-hidden focus-visible:focus-ring";

/**
 * Press feedback: only the transform animates, fast in and gentle out; colors change at once.
 * Reduced motion keeps the color change and drops the movement. The browser's tap flash is off
 * because the component draws its own.
 */
export const PRESS =
  "transition-transform duration-fast ease-standard active:duration-75 motion-reduce:transition-none [-webkit-tap-highlight-color:transparent]";

/** Scale-down while pressed, for buttons and chips. */
export const PRESS_SCALE =
  "not-data-disabled:active:scale-[0.97] motion-reduce:not-data-disabled:active:scale-100";

/** A deeper scale-down for small square targets (icon buttons, steppers). */
export const PRESS_SCALE_SMALL =
  "not-data-disabled:active:scale-[0.94] motion-reduce:not-data-disabled:active:scale-100";

/** The raised popup Select, Menu and Tooltip share. */
export const POPUP =
  "flex max-h-(--available-height) flex-col overflow-y-auto rounded-md border border-border-default bg-bg-raised p-1 shadow-float outline-none";

/** One row of a popup. */
export const POPUP_ITEM =
  "flex min-h-9 cursor-default items-center gap-2.5 rounded-sm px-2.5 font-body text-body-b2-strong text-text-secondary outline-none data-highlighted:bg-bg-hover data-highlighted:text-text-primary data-disabled:text-text-disabled";

/** A label over a popup's group (a menu's, a select's). */
export const POPUP_GROUP_LABEL =
  "truncate px-2.5 pt-1.5 pb-1 font-body text-body-b3 text-text-tertiary";

/** A line between a popup's groups (a menu's, a select's): one look for every popup. */
export const POPUP_SEPARATOR = "mx-1 my-1 h-px shrink-0 bg-border-subtle";

/** A popup's entrance and exit: a short fade and scale from its anchor. */
export const POPUP_MOTION =
  "origin-(--transform-origin) transition-[opacity,scale] duration-fast ease-standard data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transition-none";

/**
 * A text-entry well: the inset fill and the 3:1 input edge (WCAG 1.4.11), at the field radius so
 * every field in a form shares one shape. A lone bar among controls (search) takes rounded-control.
 */
export const FIELD_WELL =
  "rounded-field border border-field-edge bg-bg-inset text-text-primary hover:bg-bg-hover has-disabled:border-border-subtle has-disabled:bg-bg-surface has-disabled:text-text-disabled";

/**
 * A select's trigger: a field well, or, where the theme's controls are filled, a filled control
 * with no edge (the theme's `shape.controlStyle`, through the select tokens).
 */
export const SELECT_WELL =
  "rounded-field border border-select-edge bg-select-fill text-text-primary hover:bg-control-hover data-disabled:border-border-subtle data-disabled:bg-bg-surface data-disabled:text-text-disabled";

/** A focused well: a 2px edge in the focus color, drawn on click as well as keyboard. */
export const FIELD_FOCUS =
  "focus-within:border-focus-ring focus-within:outline-1 focus-within:-outline-offset-2 focus-within:outline-solid focus-within:outline-focus-ring";

/**
 * A card the shopper can act on (a link or a button): the edge strengthens on hover and press,
 * the card scales on press, and keyboard focus rings the whole card. The fill stays put so an
 * art well keeps its edge.
 */
export const CARD_INTERACTIVE =
  "transition-transform duration-fast ease-standard active:scale-[0.98] active:duration-75 motion-reduce:transition-none motion-reduce:active:scale-100 [-webkit-tap-highlight-color:transparent] hover:border-border-strong active:border-border-strong has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-focus-ring";

/**
 * A card's link sits on its name and stretches over the card with `::after`, so a screen reader
 * hears "name, link" rather than everything on the card. Its own ring is hidden; the card's
 * CARD_INTERACTIVE ring shows instead.
 */
export const STRETCHED_LINK = "outline-hidden after:absolute after:inset-0 after:content-['']";
